import { LIMITS } from '@roots/schema';
// Inline contribute flow — App Flow §3: auto-saved drafts, client-side resize, 5 photos max.
import { Button, Camera, PromptCard, Textarea, X, useToast } from '@roots/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { useEffect, useRef, useState } from 'react';
import { api, withQueue } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { deadlineLabel } from '../lib/format.js';
import { clearDraft, getDraft, saveDraft } from '../lib/offline.js';

interface ExistingContribution {
  id: string;
  prompt_id: string;
  status: 'draft' | 'submitted';
  text_body: string | null;
  photos: { id: string; cdn_url: string; alt_text: string | null }[];
}

export function ContributeFlow({
  familyId,
  familyName,
  cycleId,
  prompt,
  existing,
  deadline,
  expanded,
  onToggle,
  onSubmitted,
}: {
  familyId: string;
  familyName: string;
  cycleId: string;
  prompt: { id: string; text: string };
  existing: ExistingContribution | null;
  deadline: string;
  expanded: boolean;
  onToggle: () => void;
  onSubmitted: () => void;
}) {
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { user } = useAuth();
  const draftKey = existing?.id ?? `new:${prompt.id}`;
  const [text, setText] = useState(existing?.text_body ?? '');
  const [photos, setPhotos] = useState(existing?.photos ?? []);
  const [draftSaved, setDraftSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { label, urgent } = deadlineLabel(deadline);

  // Offline drafts win over the server copy until they sync (App Flow §7.3).
  useEffect(() => {
    void getDraft(draftKey).then((draft) => {
      if (draft?.text && draft.text !== existing?.text_body) setText(draft.text);
    });
  }, [draftKey, existing?.text_body]);

  async function ensureContribution(): Promise<string> {
    if (existing?.id) return existing.id;
    const created = await api.createContribution({
      prompt_id: prompt.id,
      family_id: familyId,
      text_body: text || null,
    });
    await queryClient.invalidateQueries({ queryKey: ['cycle', familyId] });
    return created.id;
  }

  const save = useMutation({
    mutationFn: async ({ submit }: { submit: boolean }) => {
      await saveDraft({
        key: draftKey,
        promptId: prompt.id,
        familyId,
        contributionId: existing?.id ?? null,
        text,
      });
      const id = await ensureContribution();
      const result = await withQueue(
        { kind: 'contribution.save', payload: { contributionId: id, text, submit } },
        () =>
          api.updateContribution(id, {
            text_body: text || null,
            ...(submit ? { status: 'submitted' as const } : {}),
          }),
      );
      if (result && submit) await clearDraft(draftKey);
      return result;
    },
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cycle', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
      if (variables.submit) {
        if (result) onSubmitted();
        else push('Saved. We’ll send it when you’re back online.');
      } else {
        setDraftSaved(true);
      }
    },
    onError: () => push('We hit a bump. Try again in a moment.', 'error'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (photos.length >= LIMITS.photosPerContribution) {
        throw new Error('Each prompt fits up to 5 photos. Remove one to add another.');
      }
      if (file.size > LIMITS.photoMaxBytes) {
        throw new Error('Photos need to be under 10MB. Try a smaller one.');
      }
      // architecture §3.1 — resize client-side so the Worker never touches an image.
      const compressed = await imageCompression(file, {
        maxWidthOrHeight: 2048,
        maxSizeMB: 2,
        fileType: 'image/webp',
        initialQuality: 0.82,
        exifOrientation: 1,
      });
      const contributionId = await ensureContribution();
      const presigned = await api.presignPhoto({
        contribution_id: contributionId,
        mime_type: 'image/webp',
        size_bytes: compressed.size,
      });
      const put = await fetch(presigned.upload_url, {
        method: 'PUT',
        body: compressed,
        headers: { 'content-type': 'image/webp' },
      });
      if (!put.ok) throw new Error('Your photo couldn’t be saved. Tap to try again.');
      return api.confirmPhoto({
        contribution_id: contributionId,
        storage_key: presigned.storage_key,
        display_order: photos.length + 1,
      });
    },
    onSuccess: (photo) => {
      setPhotos((current) => [...current, photo as never]);
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const submitted = existing?.status === 'submitted';

  return (
    <PromptCard
      familyName={familyName}
      promptText={prompt.text}
      deadlineLabel={label}
      urgent={urgent}
      submitted={submitted}
      draftSaved={draftSaved || (existing?.status === 'draft' && Boolean(existing.text_body))}
      expanded={expanded}
      onOpen={onToggle}
    >
      <div className="flex flex-col gap-3">
        <Textarea
          name={`contribution-${prompt.id}`}
          value={text}
          placeholder="Whenever you’re ready…"
          onChange={(e) => {
            setText(e.target.value);
            setDraftSaved(false);
          }}
          onBlur={() => save.mutate({ submit: false })}
          maxLength={LIMITS.contributionMaxChars}
        />

        <div className="flex flex-wrap items-center gap-2">
          {photos.map((photo) => (
            <figure key={photo.id} className="relative">
              <img
                src={photo.cdn_url}
                alt={photo.alt_text ?? `Photo by ${user?.name ?? 'you'}`}
                className="h-20 w-20 rounded-[var(--radius-sm)] object-cover"
              />
              <button
                type="button"
                aria-label="Remove photo"
                onClick={async () => {
                  await api.deletePhoto(photo.id);
                  setPhotos((current) => current.filter((p) => p.id !== photo.id));
                }}
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-bg-elevated)] shadow-[var(--shadow-card)]">
                  <X size={14} weight="bold" aria-hidden />
                </span>
              </button>
            </figure>
          ))}

          {photos.length < LIMITS.photosPerContribution && !submitted && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="type-caption flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-[var(--radius-sm)] border-2 border-dashed border-[var(--color-accent-warm-gray)] text-[var(--color-text-secondary)]"
            >
              <Camera size={24} aria-hidden />
              {upload.isPending ? 'Adding…' : 'Add photo'}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
        </div>

        {error && <p className="type-caption text-[var(--color-error)]">{error}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="type-caption">{draftSaved ? 'Draft saved' : ''}</span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onToggle}>
              Close
            </Button>
            <Button onClick={() => save.mutate({ submit: true })} loading={save.isPending}>
              {submitted ? 'Save changes' : 'Submit'}
            </Button>
          </div>
        </div>
      </div>
    </PromptCard>
  );
}
